// 3rd party
import * as React from "react";
import { LinkProps, Link } from "react-router-dom";
import { Maybe } from "tsmonad";
import {
	List,
	ListItem,
	ListItemText,
	Collapse,
	Button,
	Menu,
	MenuItem,
	Tooltip,
} from "@material-ui/core";
import {
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	ListAlt as AssetManagerIcon,
	AccountTree as ModelEditorIcon,
	Extension as RulesEngineIcon,
	People as IamIcon,
	MenuBook as DocIcon,
	LibraryBooks as DocEntryIcon,
	VerifiedUser as LicenseManagementIcon,
	Help as InfoIcon,
	Info as AboutIcon,
	BubbleChart as DefaultIcon,
} from "@material-ui/icons";
import * as immutable from "immutable";

// Domain
import * as domain from "@src/domain";

// Styles
import { useModuleListStyles } from "./styles";

export { ModuleList };

interface ModuleListProps {
	selectedModule: Maybe<domain.Module>;
	menu: domain.Menu;
	expandedGroups: immutable.Set<domain.Group["name"]>;
	onGroupClick: (group: domain.Group) => void;
	isDrawerOpen: boolean;
}

function ModuleList({
	selectedModule,
	menu,
	expandedGroups,
	onGroupClick,
	isDrawerOpen,
}: ModuleListProps): JSX.Element {
	const classes = useModuleListStyles();

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [selectedGroup, setSelectedGroup] = React.useState<Maybe<domain.Group>>(
		Maybe.nothing()
	);

	const clickMenu = (
		event: React.MouseEvent<HTMLButtonElement>,
		node: domain.Group
	): void => {
		setAnchorEl(event.currentTarget);
		setSelectedGroup(Maybe.just(node));
	};
	const closeMenu = (): void => {
		setAnchorEl(null);
	};

	const open = (node: domain.Group): boolean => expandedGroups.has(node.name);

	const selectedModuleRoute = selectedModule
		.map((module) => module.route)
		.valueOr("");
	// eslint-disable-next-line react/display-name
	const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
		(props, ref): JSX.Element => <Link innerRef={ref} {...props} />
	);

	const getListItemIcon = (
		item: domain.Module | domain.Group,
		isCollapsed: boolean
	): JSX.Element | null => {
		switch (item.type) {
			case "group": {
				switch (item.name) {
					case "Documentation": {
						return (
							<DocIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "Info": {
						return (
							<InfoIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					default:
						return null;
				}
			}
			case "module": {
				switch (item.route) {
					case "asset-manager": {
						return (
							<AssetManagerIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "model-editor": {
						return (
							<ModelEditorIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "rules-engine": {
						return (
							<RulesEngineIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "iam": {
						return (
							<IamIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "api-docs":
					case "rules-engine-docs":
					case "release-notes-docs":
					case "upgrade-notes-docs":
					case "deployment-guide-docs":
					case "reference-guide-docs": {
						return (
							<DocEntryIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "license-manager": {
						return (
							<LicenseManagementIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					case "about": {
						return (
							<AboutIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
					}
					default:
						return (
							<DefaultIcon
								className={
									isCollapsed
										? classes.iconPlaceholderSmall
										: classes.iconPlaceholder
								}
							/>
						);
				}
			}
			default:
				return null;
		}
	};

	return (
		<div className={classes.moduleListWrapper}>
			<List className={classes.moduleList}>
				{isDrawerOpen
					? menu.map(
							(node: domain.Item): JSX.Element =>
								node.type === "module" ? (
									<ListItem
										selected={selectedModuleRoute === node.route}
										key={node.name}
										component={AdapterLink}
										className={classes.moduleListItem}
										to={`/${node.route}`}
									>
										{getListItemIcon(node, false)}
										<ListItemText primary={node.name} />
									</ListItem>
								) : (
									<React.Fragment key={node.name}>
										<ListItem
											button
											className={classes.moduleListItem}
											onClick={(): void => {
												onGroupClick(node);
											}}
										>
											{getListItemIcon(node, false)}
											<ListItemText primary={node.name} />
											{open(node) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
										</ListItem>
										<Collapse in={open(node)} timeout="auto" unmountOnExit>
											{node.modules.map(
												(module: domain.Module): JSX.Element => (
													<ListItem
														selected={selectedModuleRoute === module.route}
														key={module.route}
														component={AdapterLink}
														className={classes.moduleListGroupedItem}
														to={`/${module.route}`}
													>
														{getListItemIcon(module, false)}
														<ListItemText primary={module.name} />
													</ListItem>
												)
											)}
										</Collapse>
									</React.Fragment>
								)
					  )
					: menu.map(
							(node: domain.Item): JSX.Element =>
								node.type === "module" ? (
									<Tooltip key={node.name} title={node.name} placement="right">
										<ListItem
											selected={selectedModuleRoute === node.route}
											component={AdapterLink}
											className={classes.moduleListItemSmall}
											to={`/${node.route}`}
										>
											<Button className={classes.button}>
												{getListItemIcon(node, true)}
											</Button>
										</ListItem>
									</Tooltip>
								) : (
									<React.Fragment key={node.name}>
										<Tooltip title={node.name} placement="right">
											<ListItem className={classes.moduleListItemSmall}>
												<Button
													className={classes.button}
													onClick={(event): void => clickMenu(event, node)}
												>
													{getListItemIcon(node, true)}
												</Button>
											</ListItem>
										</Tooltip>
										<Menu
											anchorEl={anchorEl}
											open={anchorEl !== null}
											onClose={closeMenu}
											keepMounted
										>
											{selectedGroup.caseOf({
												just: (group) => {
													return group.modules.map(
														(module: domain.Module): JSX.Element => (
															<MenuItem
																selected={selectedModuleRoute === module.route}
																key={module.route}
																component={AdapterLink}
																className={classes.moduleListGroupedItemSmall}
																to={`/${module.route}`}
																onClick={closeMenu}
															>
																{module.name}
															</MenuItem>
														)
													);
												},
												nothing: () => [],
											})}
										</Menu>
									</React.Fragment>
								)
					  )}
			</List>
		</div>
	);
}
